import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DevicesIcon from '@mui/icons-material/Devices';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import ApplianceDialog from './ApplianceDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Appliance {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

const AppliancesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAppliance, setSelectedAppliance] = useState<Appliance | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete Appliance ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/appliance/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'Appliance deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete appliance');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete appliance. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit Appliance ----------
  const handleEditClick = (appliance: Appliance) => {
    setSelectedAppliance(appliance);
    setEditDialogOpen(true);
  };

  // ---------- Add Appliance ----------
  const handleAddAppliance = () => {
    setSelectedAppliance(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (appliance: Appliance) => {
    setToggleLoading(appliance.id);

    try {
      const endpoint = appliance.active
        ? `api/appliance/deactivate?id=${appliance.id}`
        : `api/appliance/activate?id=${appliance.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Appliance ${appliance.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update appliance status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update appliance status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedAppliance ? 'Appliance updated successfully!' : 'Appliance created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'Appliance',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DevicesIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || (
            <span style={{ fontStyle: 'italic' }}>
              No description provided
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Appliance',
      icon: <AddIcon />,
      onClick: handleAddAppliance,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Appliances Function ----------
  const fetchAppliances = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchAppliances called with params:', params);
      try {
        const response = await DataService.get('api/appliance/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawAppliances: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawAppliances = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawAppliances = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const appliances: Appliance[] = rawAppliances.map(appliance => ({
            id: appliance.id,
            name: appliance.name,
            description: appliance.description,
            active: appliance.active
          }));

          console.log('Raw appliances from API:', rawAppliances);
          console.log('Mapped appliances:', appliances);

          // Filter by search term if provided
          let filteredAppliances = appliances;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredAppliances = appliances.filter(
              appliance =>
                appliance.name.toLowerCase().includes(searchLower) ||
                (appliance.description && appliance.description.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredAppliances.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof Appliance];
              let bValue: any = b[sortModel.field as keyof Appliance];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedAppliances = filteredAppliances.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredAppliances.length / params.pageSize);

          const result = {
            items: paginatedAppliances,
            totalCount: filteredAppliances.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch appliances: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching appliances:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load appliances. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Appliances Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Appliances"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchAppliances}
        actions={actions}
        emptyMessage="No appliances found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Appliance"
        message="Are you sure you want to delete this appliance? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit Appliance Dialog */}
      <ApplianceDialog
        open={editDialogOpen}
        appliance={selectedAppliance}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default AppliancesListing;